<template>
  <!-- Two columns for the caller's flex row: find a description on the left, what is chosen on the right. -->
  <div class="flex min-w-0 flex-1 flex-col gap-4 px-6 py-5">
    <span class="flex h-6 items-center text-sm font-semibold text-text-primary">
      {{ $t('W: Tilføj beskrivelse') }}
    </span>
    <UiTabNav
      :navItems="navItems"
      @nav-item-click="onTabClick" />
    <!-- Only the full lists need narrowing: pick one list or all of them, and search within that.
         The two share the row, half each; the fields' own bottom margin is dropped so the row
         sits right above the items. -->
    <div
      v-if="activeTab === 'lists'"
      class="flex gap-3 [&_.s-vee-form-field]:mb-0">
      <div class="min-w-0 flex-1">
        <UiComboboxVee
          name="reportDescriptionListFilter"
          :label="$t('W: Vælg liste')"
          :searchPlaceholder="$t('W: Søg efter liste...')"
          :options="listOptions"
          :modelValue="activeListFilter"
          @update:model-value="onListFilterChange" />
      </div>
      <div class="min-w-0 flex-1">
        <UiInputSearchVee
          name="reportDescriptionSearch"
          :label="searchLabel"
          :placeholder="$t('W: Søg...')"
          v-model="searchQuery" />
      </div>
    </div>
    <div
      class="min-h-0 flex-1 overflow-y-auto rounded-lg border border-border-subtle">
      <div
        v-if="visibleItems.length === 0"
        class="p-4 text-center text-sm text-text-muted">
        {{ emptyText }}
      </div>
      <label
        v-for="item in visibleItems"
        :key="item.id"
        class="flex cursor-pointer items-start gap-3 border-b border-border-subtle px-3 py-2.5 last:border-b-0 hover:bg-surface-muted">
        <input
          type="checkbox"
          class="mt-1 h-4 w-4 shrink-0 rounded border-border-subtle text-action"
          :checked="selectedItemIDs.includes(item.id)"
          @change="onToggleItem(item.id)" />
        <div class="flex min-w-0 flex-1 items-center gap-2">
          <div class="flex min-w-0 flex-1 flex-col gap-0.5">
            <span class="truncate text-sm leading-tight text-text-primary">
              {{ item.getTitle(currentLocale) }}
            </span>
            <span class="truncate text-xs leading-tight text-text-muted">
              {{ resolveItemListName(item.id) }}
            </span>
            <span
              class="truncate text-xs leading-tight"
              :class="
                snippetOf(item).text
                  ? 'text-text-muted'
                  : 'text-feedback-warning'
              ">
              {{ snippetLine(item) }}
            </span>
            <span
              v-if="warningOf(item)"
              class="truncate text-xs leading-tight text-feedback-warning">
              {{ warningOf(item) }}
            </span>
          </div>
          <UiStatusChip
            v-if="activeTab === 'lists' && suggestedItemIDs.includes(item.id)"
            variant="blue"
            :label="$t('W: Matcher')"
            isSquared />
        </div>
      </label>
    </div>
  </div>

  <div
    class="flex min-w-0 flex-1 flex-col gap-4 border-l border-border-subtle bg-surface-muted px-6 py-5">
    <div class="flex h-6 items-center gap-2">
      <span class="text-sm font-semibold text-text-primary">
        {{ $t('W: Valgte beskrivelser') }}
      </span>
      <UiStatusChip
        variant="neutral"
        :label="String(selectedItemIDs.length)"
        isSquared />
    </div>
    <!-- as tall as the tab row beside it, so the two dividers line up -->
    <span
      class="flex h-11 items-center border-b border-border-subtle text-sm text-text-muted">
      {{ subtitle }}
    </span>
    <div class="min-h-0 flex-1 overflow-y-auto">
      <UiEditableList
        :items="selectedItemIDs"
        isDraggable
        isRemovable
        :emptyStateText="$t('W: Ingen valgte beskrivelser')"
        @update:items="onItemsChange"
        @on-remove="onItemRemove">
        <template #item="{ item }">
          <div class="flex min-w-0 flex-1 items-center gap-2">
            <div class="flex min-w-0 flex-1 flex-col gap-0.5">
              <span
                class="truncate text-sm leading-tight"
                :class="
                  isItemInactive(item as string)
                    ? 'text-text-muted line-through'
                    : 'text-text-primary'
                ">
                {{ resolveItemTitle(item as string) }}
              </span>
              <span class="truncate text-xs leading-tight text-text-muted">
                {{ resolveItemListName(item as string) }}
              </span>
              <span
                class="truncate text-xs leading-tight"
                :class="
                  snippetOf(itemByID(item as string)).text
                    ? 'text-text-muted'
                    : 'text-feedback-warning'
                ">
                {{ snippetLine(itemByID(item as string)) }}
              </span>
              <span
                v-if="warningOf(itemByID(item as string))"
                class="truncate text-xs leading-tight text-feedback-warning">
                {{ warningOf(itemByID(item as string)) }}
              </span>
            </div>
            <UiStatusChip
              v-if="suggestedItemIDs.includes(item as string)"
              variant="blue"
              :label="selectedChipLabel"
              isSquared />
          </div>
        </template>
      </UiEditableList>
    </div>
  </div>
</template>

<script lang="ts">
import { mapState } from 'pinia'
import type { PropType } from 'vue'
import type { ITabNavOption } from '@team-milva/lab-components'
import type { ReportDescriptionItem } from '@/stores/reportDescriptionItemStore'
import {
  getLanguageName,
  getReportLocaleIssue,
  getTextObjSourceLocale,
} from '@/globals/typescript/utils/reportLocaleCoverage'
import { useAppStore } from '@/stores/appStore'
import { useReportDescriptionStore } from '@/stores/reportDescriptionStore'
import { useReportDescriptionItemStore } from '@/stores/reportDescriptionItemStore'

const ALL_LISTS = 'all'

/**
 * The "add descriptions to a report" body — shared by the per-sample modal and the standard
 * report's modal, so the two cannot drift apart. The caller owns the list; every edit (tick,
 * untick, remove, drag) arrives as the new full list in `on-change`.
 *
 * The report description stores must already be loaded by the caller.
 */
export default {
  name: 'ReportDescriptionsPicker',

  emits: ['on-change'],

  props: {
    /** The chosen items, in report order. */
    selectedItemIDs: { type: Array as PropType<string[]>, required: true },
    /** Items picked on the order's results — the "Forslag" tab, and what gets a "Matcher" chip. */
    suggestedItemIDs: { type: Array as PropType<string[]>, required: true },
    /** The chip on a chosen item that is one of the suggestions. */
    selectedChipLabel: { type: String, required: true },
    /**
     * The language the report is printed in. The modal itself stays in the user's language; this
     * is only used to warn when an item has no text in the report's language.
     */
    reportLocale: { type: String, required: true },
    /** The line under "Valgte beskrivelser" — what the list is attached to. */
    subtitle: { type: String, required: true },
  },

  data() {
    return {
      /** "Forslag": the suggestions. "Lister": every list, with search. */
      tab: 'suggestions' as 'suggestions' | 'lists',
      searchQuery: '',
      /** The list the "Lister" tab is narrowed to; `ALL_LISTS` is every list. */
      listFilter: ALL_LISTS,
    }
  },

  computed: {
    ...mapState(useAppStore, ['currentLocale']),
    ...mapState(useReportDescriptionStore, ['allReportDescriptions']),
    ...mapState(useReportDescriptionItemStore, ['allReportDescriptionItems']),

    // With nothing to suggest there is no "Forslag" tab, and the lists are what is shown.
    activeTab(): 'suggestions' | 'lists' {
      return this.suggestedItemIDs.length > 0 ? this.tab : 'lists'
    },

    navItems(): ITabNavOption[] {
      const items: ITabNavOption[] = []
      if (this.suggestedItemIDs.length > 0) {
        items.push({
          id: 'suggestions',
          title: this.$t('W: Forslag'),
          isActive: this.activeTab === 'suggestions',
          count: this.suggestedItemIDs.length,
        })
      }
      items.push({
        id: 'lists',
        title: this.$t('W: Lister'),
        isActive: this.activeTab === 'lists',
      })
      return items
    },

    // The lists that can be picked from: active, still using descriptions, and with something in them.
    listOptions(): { value: string; text: string }[] {
      const usedListIDs = new Set(
        this.usableItems.map((x) => x.reportDescriptionID)
      )
      const lists = this.allReportDescriptions
        .filter((x) => usedListIDs.has(x.id))
        .map((x) => ({ value: x.id, text: x.getName(this.currentLocale) }))
        .sort((a, b) => a.text.localeCompare(b.text))
      return [{ value: ALL_LISTS, text: this.$t('W: Alle lister') }, ...lists]
    },

    // "Søg i alle lister" while every list is searched, "Søg i liste" once it is narrowed to one
    searchLabel(): string {
      return this.activeListFilter === ALL_LISTS
        ? this.$t('W: Søg i alle lister')
        : this.$t('W: Søg i liste')
    },

    // A list that is no longer there (deactivated meanwhile) falls back to all of them.
    activeListFilter(): string {
      return this.listOptions.some((x) => x.value === this.listFilter)
        ? this.listFilter
        : ALL_LISTS
    },

    // Active items in a collection that still uses descriptions and is active — what can be picked.
    usableItems(): ReportDescriptionItem[] {
      const usableDescriptionIDs = new Set(
        this.allReportDescriptions
          .filter(
            (x) =>
              x.activeState === 'active' &&
              (x.options?.usesDescriptions ?? true)
          )
          .map((x) => x.id)
      )
      return this.allReportDescriptionItems.filter(
        (item) =>
          item.activeState === 'active' &&
          usableDescriptionIDs.has(item.reportDescriptionID)
      )
    },

    // "Forslag": every suggestion, ticked or not — there is no search there. "Lister": every usable
    // item, alphabetically, narrowed by the search. A ticked item stays in view — unticking it is
    // how it is taken out.
    visibleItems(): ReportDescriptionItem[] {
      if (this.activeTab === 'suggestions') {
        return this.suggestedItemIDs
          .map((id) => this.allReportDescriptionItems.find((x) => x.id === id))
          .filter((x): x is ReportDescriptionItem => !!x)
      }
      const base = this.usableItems
        .filter(
          (x) =>
            this.activeListFilter === ALL_LISTS ||
            x.reportDescriptionID === this.activeListFilter
        )
        .sort((a, b) =>
          a
            .getTitle(this.currentLocale)
            .localeCompare(b.getTitle(this.currentLocale))
        )
      const query = this.searchQuery.trim().toLowerCase()
      if (!query) return base
      return base.filter(
        (item) =>
          item.getTitle(this.currentLocale).toLowerCase().includes(query) ||
          this.resolveItemListName(item.id).toLowerCase().includes(query)
      )
    },

    emptyText(): string {
      return this.$t('W: Ingen elementer')
    },
  },

  methods: {
    // A new list starts with a clean search — what was typed was for the previous one.
    onListFilterChange(listID: string): void {
      this.listFilter = listID
      this.searchQuery = ''
    },

    onTabClick(id: string): void {
      this.tab = id as 'suggestions' | 'lists'
    },

    // Ticking adds the item at the end of the list; unticking takes it out (and, for a suggestion,
    // leaves it unticked in "Forslag" where it can be ticked back).
    onToggleItem(itemID: string): void {
      this.$emit(
        'on-change',
        this.selectedItemIDs.includes(itemID)
          ? this.selectedItemIDs.filter((x) => x !== itemID)
          : [...this.selectedItemIDs, itemID]
      )
    },

    onItemsChange(items: unknown[]): void {
      this.$emit('on-change', items as string[])
    },

    onItemRemove(item: unknown): void {
      this.$emit(
        'on-change',
        this.selectedItemIDs.filter((x) => x !== (item as string))
      )
    },

    itemByID(itemID: string): ReportDescriptionItem | undefined {
      return this.allReportDescriptionItems.find((x) => x.id === itemID)
    },

    // A one-line plain-text taste of the description, in the user's language — or, when it has none
    // there, in the language it does have, tagged ("EN: …") so it is clear it is not theirs.
    snippetOf(item: ReportDescriptionItem | undefined): {
      text: string
      tag: string
    } {
      if (!item) return { text: '', tag: '' }
      const source = getTextObjSourceLocale(item.text, this.currentLocale)
      if (!source) return { text: '', tag: '' }
      const text = (item.text[source] ?? '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
      // When the report is in the user's own language the warning line already says it
      const isTagged =
        source !== this.currentLocale &&
        this.reportLocale !== this.currentLocale
      return { text, tag: isTagged ? source.toUpperCase() : '' }
    },

    snippetLine(item: ReportDescriptionItem | undefined): string {
      const { text, tag } = this.snippetOf(item)
      if (!text) return this.$t('W: Ingen beskrivelse')
      return tag ? `${tag}: ${text}` : text
    },

    // Said when the report will print this item in another language than its own — it falls back to
    // English, or to whatever language has text, without a word to anyone.
    warningOf(item: ReportDescriptionItem | undefined): string {
      if (!item) return ''
      const issue = getReportLocaleIssue(item, this.reportLocale)
      if (!issue) return ''
      const params = {
        language: getLanguageName(this.reportLocale, this.currentLocale),
        fallback: getLanguageName(issue.fallbackLocale, this.currentLocale),
      }
      if (issue.missing === 'text')
        return this.$t(
          'W: Mangler {language} tekst – rapporten bruger {fallback}',
          params
        )
      if (issue.missing === 'title')
        return this.$t(
          'W: Mangler {language} titel – rapporten bruger {fallback}',
          params
        )
      return this.$t(
        'W: Mangler {language} titel og tekst – rapporten bruger {fallback}',
        params
      )
    },

    isItemInactive(itemID: string): boolean {
      const item = this.allReportDescriptionItems.find((x) => x.id === itemID)
      return item ? item.activeState !== 'active' : false
    },

    resolveItemTitle(itemID: string): string {
      const item = this.allReportDescriptionItems.find((x) => x.id === itemID)
      return item ? item.getTitle(this.currentLocale) : itemID
    },

    resolveItemListName(itemID: string): string {
      const item = this.allReportDescriptionItems.find((x) => x.id === itemID)
      if (!item) return ''
      const description = this.allReportDescriptions.find(
        (x) => x.id === item.reportDescriptionID
      )
      return description ? description.getName(this.currentLocale) : ''
    },
  },
}
</script>
